import React, { useState, useEffect, useCallback } from 'react';
import { useAuthQuery as useQuery } from '../../../../shared/hooks/useAuthQuery';
import { useNavigate } from 'react-router-dom';
import { api } from '../../../../convex/_generated/api';
import { Button } from '../../../../catalyst-components/button';
import { Spinner } from '../../../../catalyst-components/spinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../catalyst-components/table';
import { Badge } from '../../../../catalyst-components/badge';
import { DailyStatsChart } from '../../../../shared/components/DailyStatsChart';

export const Subscribers = () => {
  const navigate = useNavigate();
  const [allSubscribers, setAllSubscribers] = useState<any[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const subscribersCount = useQuery(api.subscribers.getActiveSubscribersCount);
  const dailyStats = useQuery(api.subscribers.getSubscribersDailyStats, { days: 30, includeInactive: false });
  
  // Use larger page size for initial load, smaller for subsequent loads
  const pageSize = isInitialLoad ? 20 : 10;
  
  const paginatedResult = useQuery(api.subscribers.getSubscribersPaginated, {
    paginationOpts: {
      numItems: pageSize,
      cursor: cursor,
    }
  });

  // Load initial data
  useEffect(() => {
    if (paginatedResult) {
      if (cursor === null) {
        // Initial load
        setAllSubscribers(paginatedResult.page);
        setIsInitialLoad(false);
      } else {
        // Append new data only if not already added
        setAllSubscribers(prev => {
          const newIds = new Set(prev.map(sub => sub._id));
          const newItems = paginatedResult.page.filter(item => !newIds.has(item._id));
          return [...prev, ...newItems];
        });
      }
      setHasMore(paginatedResult.isDone === false);
      setLoading(false);
    }
  }, [paginatedResult, cursor]);

  const loadMore = useCallback(() => {
    if (paginatedResult && hasMore && !loading) {
      setLoading(true);
      setCursor(paginatedResult.continueCursor);
    }
  }, [paginatedResult, hasMore, loading]);

  // Infinite scroll handler and auto-load if content doesn't fill screen
  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop >=
        document.documentElement.offsetHeight - 100
      ) {
        loadMore();
      }
    };

    // Check if content fills the screen, if not, load more automatically
    const checkAndLoadMore = () => {
      if (hasMore && !loading && document.documentElement.scrollHeight <= window.innerHeight + 50) {
        loadMore();
      }
    };

    window.addEventListener('scroll', handleScroll);
    
    // Check if we need to auto-load more content after a short delay
    const timeoutId = setTimeout(checkAndLoadMore, 100);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timeoutId);
    };
  }, [loadMore, hasMore, loading, allSubscribers.length]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <section className="min-h-screen bg-background pt-24 px-6 flex flex-col justify-start">
      <h1 className="text-3xl font-bold mb-4">Subscribers</h1>
      <div className="flex flex-row gap-4 mb-6">
        <Button color="light" onClick={() => navigate('../')}>
          Back to admin
        </Button>
        <Badge color="primary-5">
          Total: {subscribersCount ?? 0}
        </Badge>
      </div>

      {/* Chart Section */}
      {dailyStats && (
        <div className="mb-8">
          <DailyStatsChart 
            data={dailyStats} 
            title="Daily New Subscribers (Last 30 Days)"
            yAxisLabel="New Subscribers"
            tooltipSuffix="new subscribers"
            lineColor="#3b82f6"
          />
        </div>
      )}

      <div>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader>Email</TableHeader>
              <TableHeader>IP Address</TableHeader>
              <TableHeader>Client ID</TableHeader>
              <TableHeader>Location/Timezone</TableHeader>
              <TableHeader>Subscribed Date</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {allSubscribers.map((subscriber, index) => (
              <TableRow key={`${subscriber._id}-${index}`}>
                <TableCell className="font-medium">
                  {subscriber.email}
                </TableCell>
                <TableCell className="text-gray-600 font-mono text-sm">
                  {subscriber.ip_address || 'N/A'}
                </TableCell>
                <TableCell className="text-gray-600 font-mono text-xs">
                  {subscriber.client_id ? subscriber.client_id : 'N/A'}
                </TableCell>
                <TableCell className="text-gray-600 text-sm">
                  {subscriber.timezone || 'N/A'}
                </TableCell>
                <TableCell className="text-gray-500">
                  {formatDate(subscriber.subscribed_at)}
                </TableCell>
              </TableRow>
            ))}
            {loading && (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8">
                  <div className="flex justify-center items-center">
                    <Spinner className="w-6 h-6 mr-2" />
                    Loading more subscribers...
                  </div>
                </TableCell>
              </TableRow>
            )}
            {!hasMore && allSubscribers.length > 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-4 text-gray-500">
                  No more subscribers to load
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {allSubscribers.length === 0 && !loading && (
          <div className="text-center py-12 text-gray-500">
            <p>No subscribers yet</p>
          </div>
        )}
      </div>
    </section>
  );
}; 